import { computed, ref } from "vue";
import { defineStore } from "pinia";
import UserSettingsService from "@/services/UserSettingsService";
import {
  HOTKEY_DEFAULTS,
  HOTKEY_DEFINITIONS,
  normalizeHotkey,
  type UserSettingKey,
} from "@/services/UserSettingsHotkeys";

export const useUserSettingsStore = defineStore("user-settings", () => {
  const settings = ref<Partial<Record<UserSettingKey, string>>>({});
  const isLoading = ref(false);
  const isResolved = ref(false);
  const error = ref("");
  const loadedForUserId = ref<string | null>(null);

  const hotkeys = computed<Record<UserSettingKey, string> | null>(() => {
    if (!isResolved.value) return null;

    return HOTKEY_DEFINITIONS.reduce(
      (values, definition) => ({
        ...values,
        [definition.key]:
          settings.value[definition.key] ?? HOTKEY_DEFAULTS[definition.key],
      }),
      {} as Record<UserSettingKey, string>
    );
  });

  function hotkeyValue(key: UserSettingKey): string | undefined {
    return hotkeys.value?.[key];
  }

  async function load(
    userId: number | string | null | undefined
  ): Promise<boolean> {
    const normalizedUserId = String(userId ?? "");
    if (!normalizedUserId || normalizedUserId === "0") {
      reset();
      return false;
    }

    if (isLoading.value) return false;
    if (isResolved.value && loadedForUserId.value === normalizedUserId) {
      return error.value === "";
    }

    isLoading.value = true;
    isResolved.value = false;
    error.value = "";
    settings.value = {};
    loadedForUserId.value = normalizedUserId;

    const result = await UserSettingsService.load();
    if (result.success) {
      settings.value = result.settings;
    } else {
      error.value = result.message;
    }

    isResolved.value = true;
    isLoading.value = false;
    return result.success;
  }

  async function retry(
    userId: number | string | null | undefined
  ): Promise<boolean> {
    isResolved.value = false;
    return load(userId);
  }

  async function saveHotkey(
    key: UserSettingKey,
    value: string
  ): Promise<boolean> {
    const normalized = normalizeHotkey(value);
    if (!normalized.valid) {
      error.value = normalized.error;
      return false;
    }

    const result = await UserSettingsService.save(key, normalized.value);
    if (!result.success) {
      error.value = result.message;
      return false;
    }

    settings.value = {
      ...settings.value,
      [result.setting.key]: result.setting.value,
    };
    error.value = "";
    isResolved.value = true;
    return true;
  }

  function reset(): void {
    settings.value = {};
    isLoading.value = false;
    isResolved.value = false;
    error.value = "";
    loadedForUserId.value = null;
  }

  return {
    error,
    hotkeys,
    hotkeyValue,
    isLoading,
    isResolved,
    load,
    loadedForUserId,
    reset,
    retry,
    saveHotkey,
    settings,
  };
});
